import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Mic } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';

/**
 * Global floating action button → /sermon-prep
 * Visible on every authed route EXCEPT the sermon-prep itself and the landing/login pages.
 * Sits above the AI companion FAB without overlapping it.
 */
const HIDDEN_PATHS = [
  '/sermon-prep',
  '/sermon-prep/present',
  '/login',
  '/sample',
  '/',  // landing
];

const SermonFloatingButton = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { language } = useLanguage();
  const { user } = useAuth() || {};
  const [tooltipOpen, setTooltipOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    const seen = localStorage.getItem('lumera-sermon-fab-seen');
    if (!seen) {
      setTooltipOpen(true);
      const t = setTimeout(() => {
        setTooltipOpen(false);
        localStorage.setItem('lumera-sermon-fab-seen', '1');
      }, 5000);
      return () => clearTimeout(t);
    }
  }, [user]);

  if (!user) return null;
  if (HIDDEN_PATHS.some((p) => location.pathname === p || location.pathname.startsWith(`${p}/`))) {
    return null;
  }

  return (
    <div
      className="fixed bottom-24 right-5 z-30 flex flex-col items-end gap-2 sm:bottom-28"
      data-testid="sermon-fab-wrapper"
    >
      {tooltipOpen && (
        <div className="bg-slate-900 text-white text-xs font-medium rounded-lg px-3 py-2 shadow-lg max-w-[180px] animate-in fade-in slide-in-from-right-2 duration-300">
          {language === 'es'
            ? 'Toca para iniciar la preparación de un sermón'
            : 'Tap to start preparing a sermon'}
          <div className="absolute -bottom-1 right-5 w-2 h-2 bg-slate-900 rotate-45" />
        </div>
      )}
      <button
        onClick={() => navigate('/sermon-prep')}
        data-testid="sermon-fab-btn"
        className="group flex items-center gap-2 bg-gradient-to-br from-amber-500 to-orange-600 text-white rounded-full shadow-xl hover:shadow-2xl hover:scale-105 active:scale-95 transition-all h-12 px-4 sm:px-5 border-2 border-white/20"
        aria-label={language === 'es' ? 'Preparar Sermón' : 'Sermon Prep'}
      >
        <Mic className="w-5 h-5 shrink-0" />
        <span className="text-sm font-bold whitespace-nowrap hidden sm:inline">
          {language === 'es' ? 'Sermón' : 'Sermon'}
        </span>
      </button>
    </div>
  );
};

export default SermonFloatingButton;
